import { useEffect, useState } from "react";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { hasStepUpMethod } from "@/shared/utils/stepUp";

import { IdentitiesService } from "../services/identitiesService";

/**
 * Where the organization stands on a holder a card can be issued to:
 *
 * - `ready` — at least one holder is ACTIVE with its KYC approved (what `POST /internal/cards` checks).
 * - `in_review` — holders exist and none is issuable yet: the KYC or the card-provider registration is pending.
 * - `missing` — no holder, or only rejected ones.
 * - `unknown` — the caller cannot read holders (`identities:read`) or the read failed; the step shows no
 *   status.
 */
export type FirstCardHolderStatus = "ready" | "in_review" | "missing" | "unknown";

export interface FirstCardRequirements {
  hasSecurityMethod: boolean;
  holderStatus: FirstCardHolderStatus;
}

/**
 * What issuing the organization's first card still needs. Only asked while the organization has no card, so
 * an organization that already issued one never pays for the two counts.
 *
 * Two counts because one cannot answer it: the KYC buckets ignore the identity `status`, and an approved KYC
 * whose card-provider registration is still `PENDING_REVIEW` cannot be issued a card yet.
 */
export function useFirstCardRequirements(enabled: boolean): FirstCardRequirements {
  const { user, can } = useAuth();
  const canReadHolders = can("identities:read");
  const [holderStatus, setHolderStatus] = useState<FirstCardHolderStatus>("unknown");

  useEffect(() => {
    if (!enabled || !canReadHolders) return;
    let cancelled = false;
    (async () => {
      try {
        const [all, active] = await Promise.all([
          IdentitiesService.fetchIdentitiesCounts({}),
          IdentitiesService.fetchIdentitiesCounts({ status: "ACTIVE" }),
        ]);
        if (cancelled) return;
        if (active.data.approved > 0) {
          setHolderStatus("ready");
        } else if (all.data.all - all.data.rejected > 0) {
          setHolderStatus("in_review");
        } else {
          setHolderStatus("missing");
        }
      } catch {
        // The step still tells what is needed; it just cannot say how far along the organization is.
        if (!cancelled) setHolderStatus("unknown");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled, canReadHolders]);

  return { hasSecurityMethod: hasStepUpMethod(user), holderStatus };
}
